import {
  LayoutDashboard,
  Users,
  Truck,
  FileText,
  Settings,
  Car,
  ArrowLeftRight,
  ChevronRight
} from 'lucide-react';
import LogoutButton from '../../../components/admin/LogoutAdmin';
import AllMap from './location/AllMap';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const Sidebar = ({ activeTab, setActiveTab }: SidebarProps) => {
  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'transports',
      label: 'Transporturi',
      icon: <Truck className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'clients',
      label: 'Clienți',
      icon: <Users className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'vehicule',
      label: 'Vehicule & GPS',
      icon: <Car className="w-5 h-5" />,
      component: AllMap,
      badge: null
    },
    {
      id: 'transferuri',
      label: 'Transferuri',
      icon: <ArrowLeftRight className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'management',
      label: 'Management',
      icon: <FileText className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'cmr',
      label: 'CMR',
      icon: <FileText className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'settings',
      label: 'Setări',
      icon: <Settings className="w-5 h-5" />,
      badge: null
    },
  ];

  return (
    <>
      {/* Sidebar Desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col bg-white shadow-2xl relative overflow-hidden border-r border-gray-100">
          {/* Decorative gradient overlay */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none"></div>
          
          {/* Logo Section */}
          <div className="relative z-10 flex items-center justify-between h-20 px-6 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-500 blur-xl opacity-20 rounded-full"></div>
                <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30 transform hover:rotate-6 hover:scale-110 transition-all duration-300">
                  <img src="/logo.png" alt="VGL Logo" className="h-7 w-7" />
                </div>
              </div>
              <div className="ml-3">
                <span className="block text-xl font-bold text-gray-900">
                  VGL Admin
                </span>
                <span className="block text-xs text-gray-500 font-medium">Transport Management</span>
              </div>
            </div>
          </div>

          {/* Menu */}
          <nav className="relative z-10 flex-1 px-4 py-6 overflow-y-auto custom-scrollbar">
            <div className="space-y-1.5">
              {menuItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  style={{ animationDelay: `${index * 40}ms` }}
                  className={`group w-full flex items-center justify-between px-4 py-3.5 text-left rounded-2xl transition-all duration-300 relative overflow-hidden animate-slideIn ${
                    activeTab === item.id
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-xl shadow-orange-500/30'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {/* Shimmer effect pentru active */}
                  {activeTab === item.id && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                  )}
                  
                  {/* Hover gradient background */}
                  {activeTab !== item.id && (
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-50/0 via-orange-50/50 to-red-50/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                  
                  <div className="flex items-center relative z-10">
                    {/* Icon container */}
                    <div className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${
                      activeTab === item.id 
                        ? 'bg-white/25 backdrop-blur-sm scale-110' 
                        : 'bg-gray-100 group-hover:bg-white group-hover:shadow-md group-hover:scale-110'
                    }`}>
                      {item.icon}
                    </div>
                    
                    {/* Label */}
                    <span className={`ml-4 font-semibold text-sm tracking-wide transition-all duration-300 ${
                      activeTab === item.id ? 'translate-x-1' : ''
                    }`}>
                      {item.label}
                    </span>
                  </div>
                  
                  {/* Arrow indicator */}
                  <ChevronRight className={`w-5 h-5 relative z-10 transition-all duration-300 ${
                    activeTab === item.id 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 -translate-x-2 group-hover:opacity-40 group-hover:translate-x-0'
                  }`} />
                  
                  {/* Active side bar */}
                  {activeTab === item.id && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-r-full shadow-lg"></div>
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* Logout Section */}
          <div className="relative z-10 p-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
            <div className="transform hover:scale-105 transition-transform duration-300">
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden bg-white/98 backdrop-blur-xl border-t border-gray-200 z-50 shadow-2xl">
        <div className="relative">
          {/* Top gradient line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500"></div>
          
          <div className="flex justify-around items-center py-3 px-2">
            {menuItems.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`flex flex-col items-center justify-center p-2 min-w-0 flex-1 transition-all duration-300 relative ${
                  activeTab === item.id
                    ? 'text-white'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {/* Icon container with gradient background when active */}
                <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-500 ${
                  activeTab === item.id 
                    ? 'bg-gradient-to-br from-orange-500 to-red-500 shadow-xl shadow-orange-500/50 transform -translate-y-2 scale-110' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}>
                  <div className={activeTab === item.id ? 'text-white' : 'text-gray-600'}>
                    {item.icon}
                  </div>
                  
                  {/* Pulse ring for active */}
                  {activeTab === item.id && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 animate-ping opacity-20"></div>
                  )}
                </div>
                
                {/* Label with slide animation */}
                <span 
                  className={`relative z-10 text-xs font-bold truncate max-w-full transition-all duration-300 ${
                    activeTab === item.id 
                      ? 'opacity-100 mt-1.5 text-gray-900' 
                      : 'opacity-0 absolute'
                  }`}
                >
                  {item.label}
                </span>
                
                {/* Active dot indicator */}
                {activeTab === item.id && (
                  <div className="absolute -bottom-1 w-10 h-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-lg"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }

        .animate-shimmer {
          animation: shimmer 2.5s infinite;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(243, 244, 246, 0.5);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #f97316, #ef4444);
          border-radius: 10px;
          border: 1px solid rgba(243, 244, 246, 0.5);
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #ea580c, #dc2626);
        }
      `}</style>
    </>
  );
};

export default Sidebar;